import NewTabLink from '@/components/atoms/NewTabLink';
import { ErrorModal, LoadingModal, StandardModal } from '@/components/molecules/common/Modal';
import { useErrorHandling } from '@/hooks/common';
import { useItemCurationStatus } from '@/hooks/item';
import { Hashes, Tab } from '@/types/hashes';
import { CurationMatch, CurationPhotoMatch, CurationPhotoMatchTier } from '@/types/item';
import { ItemCurationStatusModal } from '@/types/modal';
import { transformDateString } from '@/utilities/datetime';
import { buildItemPhotoUrl } from '@/utilities/image';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { alpha, Box, SxProps, Theme, Tooltip } from '@mui/material';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ItemCurationStatus.module.scss';

const buildTabUrl = (tab: Tab, valueKey: Hashes, value: number) => `#${Hashes.TAB}=${tab}&${valueKey}=${value}`;

const formatSimilarity = (similarity: number) => {
  // Round away float error (e.g. 0.57 * 10000 = 5699.999…) before truncating to 2 decimals
  const truncated = Math.trunc(Math.round(similarity * 1e6) / 100) / 10000;
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

type CurationMatchStatus = 'sold' | 'on_sale' | 'inactive';

const getMatchStatus = ({ isSold, isActive }: CurationMatch): CurationMatchStatus | null => {
  if (isSold === true) return 'sold';
  if (isSold === false && isActive === true) return 'on_sale';
  if (isSold === false && isActive === false) return 'inactive';
  return null;
};

const statusSx: Record<CurationMatchStatus, SxProps<Theme>> = {
  sold: { bgcolor: 'grey.200', color: 'grey.700' },
  on_sale: { bgcolor: (theme) => alpha(theme.palette.success.main, 0.12), color: 'success.dark' },
  inactive: { bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), color: 'error.main' },
};

// Photos can be deleted during curation: name may be missing, or the file may 404
const CurationPhoto: React.FC<{ name?: string; size: number; className?: string; compact?: boolean }> = ({
  name,
  size,
  className,
  compact,
}) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (!name || failed) {
    const label = t('modal.item_curation_status.photo_unavailable');
    return (
      <div
        className={clsx([className, styles.photoMissing])}
        title={name ?? label}
        data-cy={'curation-photo-missing'}
      >
        <ImageNotSupportedOutlinedIcon fontSize={compact ? 'small' : 'medium'} />
        {!compact && <span>{label}</span>}
      </div>
    );
  }

  return (
    <img
      className={className}
      src={buildItemPhotoUrl(name, size)}
      alt={name}
      onError={() => setFailed(true)}
      data-cy={'curation-photo'}
    />
  );
};

const PhotoPair: React.FC<{ photo: CurationPhotoMatch }> = ({ photo }) => {
  const { t } = useTranslation();
  const isExact = photo.tier === CurationPhotoMatchTier.exact;

  return (
    <div
      className={styles.pair}
      data-cy={'curation-photo-pair'}
    >
      <span
        className={clsx([styles.tier, !isExact && styles.tierNear])}
        data-cy={`curation-photo-pair-tier-${photo.tier}`}
      >
        {isExact
          ? t('modal.item_curation_status.exact')
          : [t('modal.item_curation_status.near'), photo.similarity != null && `${formatSimilarity(photo.similarity)}`]
              .filter(Boolean)
              .join(' · ')}
      </span>
      <div className={styles.pairImages}>
        <CurationPhoto
          name={photo.source_photos[0]}
          size={300}
          className={clsx([styles.photo, styles.sourcePhoto])}
        />
        <SyncAltIcon className={styles.pairIcon} />
        <CurationPhoto
          name={photo.matched_photos[0]}
          size={300}
          className={styles.photo}
        />
      </div>
      <div className={styles.pairNames}>
        <div data-cy={'curation-photo-pair-source-names'}>
          {photo.source_photos.map((name) => (
            <div key={name}>{name}</div>
          ))}
        </div>
        <div
          className={styles.pairNamesMatched}
          data-cy={'curation-photo-pair-matched-names'}
        >
          {photo.matched_photos.map((name) => (
            <div key={name}>{name}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

const View: React.FC<{ modal: ItemCurationStatusModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;
  const { data, isLoading, isError, error } = useItemCurationStatus(item.productId);
  const { getErrorMsgFromResponse } = useErrorHandling();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const title = t('modal.item_curation_status.title');

  if (isLoading) {
    return (
      <LoadingModal
        title={title}
        onClose={onClose}
      />
    );
  }

  if (isError || !data) {
    return (
      <ErrorModal
        title={title}
        message={getErrorMsgFromResponse(error) ?? t('modal.item_curation_status.no_matches')}
        onClose={onClose}
      />
    );
  }

  const matches = [...data.matches].sort((a, b) => b.matched_images - a.matched_images);
  const selected = matches.find((match) => match.product_id === selectedId) ?? matches[0];

  const allPhotos = matches.flatMap((match) => match.photos);
  const exactCount = allPhotos.filter((photo) => photo.tier === CurationPhotoMatchTier.exact).length;
  const nearCount = allPhotos.length - exactCount;

  const renderRelation = (match: CurationMatch, dataCy: string) => {
    if (match.seller_id == null || data.seller_id == null) return null;
    const isSameSeller = match.seller_id === data.seller_id;
    return (
      <span
        className={clsx(styles.relation, { [styles.danger]: !isSameSeller })}
        data-cy={dataCy}
      >
        {isSameSeller ? t('modal.item_curation_status.same_seller') : t('modal.item_curation_status.different_seller')}
      </span>
    );
  };

  const renderStatus = (match: CurationMatch) => {
    const status = getMatchStatus(match);
    if (!status) return null;
    return (
      <Box
        component="span"
        data-cy={'curation-match-item-status'}
        sx={{
          px: 1,
          py: 0.125,
          borderRadius: '1rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          ...statusSx[status],
        }}
      >
        {t(`modal.item_curation_status.status.${status}`)}
      </Box>
    );
  };

  const daysEarlier = (match: CurationMatch) => {
    if (!match.deposit_date || !data.deposit_date) return null;
    return dayjs(data.deposit_date).diff(dayjs(match.deposit_date), 'day');
  };

  const summary: { id: string; label: string; value: React.ReactNode; tooltip?: string; danger?: boolean }[] = [
    {
      id: 'deposit-verdict',
      label: t('modal.item_curation_status.deposit_verdict'),
      value: data.deposit_verdict ?? '-',
      danger: true,
    },
    {
      id: 'duplicate-products',
      label: t('modal.item_curation_status.duplicate_products'),
      value: t('modal.item_curation_status.items_count', { count: matches.length }),
    },
    {
      id: 'match-type',
      label: t('modal.item_curation_status.match_type'),
      value: t('modal.item_curation_status.match_type_value', { exact: exactCount, near: nearCount }),
      tooltip: t('modal.item_curation_status.match_type_legend'),
    },
  ];

  const renderDetail = (match: CurationMatch) => {
    const days = daysEarlier(match);
    return (
      <>
        <NewTabLink
          href={buildTabUrl(Tab.ITEM, Hashes.ITEM_VALUE, match.product_id)}
          label={`#${match.product_id}`}
          sx={{ fontSize: '0.875rem', fontWeight: 600 }}
          dataCy={'curation-detail-product-link'}
        />
        <div
          className={styles.detailName}
          data-cy={'curation-detail-brand'}
        >
          <b>{match.brand}</b>
        </div>
        <div
          className={styles.detailName}
          data-cy={'curation-detail-name'}
        >
          {match.name}
        </div>
        <dl className={styles.detailInfo}>
          <dt>{t('modal.item_curation_status.seller')}</dt>
          <dd data-cy={'curation-detail-seller'}>
            {match.seller_id ? (
              <NewTabLink
                href={buildTabUrl(Tab.LOGIN_ID, Hashes.LOGIN_ID_VALUE, match.seller_id)}
                label={`#${match.seller_id}`}
                dataCy={'curation-detail-seller-link'}
              />
            ) : (
              '-'
            )}
          </dd>
          <dt>{t('modal.item_curation_status.relation')}</dt>
          <dd>{renderRelation(match, 'curation-detail-relation')}</dd>
          <dt>{t('modal.item_curation_status.deposited')}</dt>
          <dd data-cy={'curation-detail-deposited'}>
            {transformDateString(match.deposit_date, 'DD/MM/YYYY, HH:mm') ?? '-'}
            {days != null && days >= 0 && (
              <>
                {` · ${t('modal.item_curation_status.days_earlier', { count: days })}`}
                <Tooltip
                  title={t('modal.item_curation_status.days_earlier_tooltip', {
                    count: days,
                    matchId: match.product_id,
                    currentId: data.product_id,
                  })}
                  arrow
                >
                  <ErrorOutlineIcon sx={{ fontSize: '1rem', cursor: 'help', verticalAlign: 'text-bottom', ml: 0.5 }} />
                </Tooltip>
              </>
            )}
          </dd>
        </dl>
        <div className={styles.sectionTitle}>
          {t('modal.item_curation_status.duplicated_photos_list', { count: match.photos.length })}
        </div>
        <div
          className={styles.pairs}
          data-cy={'curation-photo-pairs'}
        >
          {match.photos.map((photo, index) => (
            <PhotoPair
              key={`${photo.image_name ?? ''}-${photo.matched_image_name ?? index}`}
              photo={photo}
            />
          ))}
        </div>
      </>
    );
  };

  const body = (
    <>
      <div
        className={styles.summary}
        data-cy={'curation-summary'}
      >
        {summary.map(({ id, label, value, tooltip, danger }) => (
          <div
            key={id}
            className={styles.summaryCell}
            data-cy={`curation-summary-${id}`}
          >
            <div className={styles.summaryLabel}>
              {label}
              {tooltip && (
                <Tooltip
                  title={tooltip}
                  arrow
                  slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: '1rem', cursor: 'help' }} />
                </Tooltip>
              )}
            </div>
            <div
              className={clsx(styles.summaryValue, { [styles.danger]: danger })}
              data-cy={'curation-summary-value'}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
      {selected ? (
        <div className={styles.columns}>
          <div className={styles.listColumn}>
            <div className={styles.sectionTitle}>
              {t('modal.item_curation_status.duplicate_products_list', { count: matches.length })}
            </div>
            <div
              className={styles.list}
              data-cy={'curation-match-list'}
            >
              {matches.map((match) => (
                <button
                  key={match.product_id}
                  type="button"
                  data-cy={`curation-match-item-${match.product_id}`}
                  aria-pressed={match.product_id === selected.product_id}
                  className={clsx([
                    styles.listItem,
                    match.product_id === selected.product_id && styles.listItemSelected,
                  ])}
                  onClick={() => setSelectedId(match.product_id)}
                >
                  <CurationPhoto
                    name={match.photos[0]?.matched_photos[0]}
                    size={120}
                    className={styles.thumbnail}
                    compact
                  />
                  <span className={styles.listItemInfo}>
                    <span className={styles.listItemIdRow}>
                      <span className={styles.listItemId}>#{match.product_id}</span>
                      {renderStatus(match)}
                    </span>
                    <Box sx={{ py: 0.5 }}>
                      <b>{match.brand}</b>
                      <div>{match.name}</div>
                    </Box>
                    {renderRelation(match, 'curation-match-item-relation')}
                  </span>
                  <span className={styles.count}>
                    <span
                      className={styles.countBadge}
                      data-cy={'curation-match-item-count'}
                    >
                      {match.matched_images}
                    </span>
                    {t('modal.item_curation_status.photos')}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div
            className={styles.detail}
            data-cy={`curation-match-detail-${selected.product_id}`}
          >
            {renderDetail(selected)}
          </div>
        </div>
      ) : (
        <p data-cy={'curation-no-matches'}>{t('modal.item_curation_status.no_matches')}</p>
      )}
    </>
  );

  return (
    <StandardModal
      title={title}
      onClose={onClose}
      onSubmit={onClose}
      submittable={false}
      ignoreSubmitLabel
      closeLabel={t('common.close').toUpperCase()}
      body={body}
      containerStyle={{ width: 1400, maxWidth: '90vw', height: '80vh' }}
      modalName="item_curation_status"
    />
  );
};

export default View;
